import { MobXProviderContext } from 'mobx-react';
import { ContactModelBase } from 'Models/ContactModel';
import { ContactModelV2 } from 'Models/ContactModelV2';
import { useContext, useMemo } from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { formatNumberWithNationalCode } from 'Utils/phoneUtil';
import { MessageStatusModel, PresenceModel } from '../../../../../../models';
import { mapContactCategory } from '../../../utils/map_contact_category';

// Helper functions outside the hook to avoid recreation on each render
const getEntityName = (
  person?: ContactModelV2 | null,
  contacts?: ContactModelV2[] | null,
  unsavedContact?: ContactModelBase | null
): string => {
  try {
    if (person) {
      return person.name;
    }

    if (contacts?.[0]) {
      return contacts[0]?.name || '';
    }

    return '';
  } catch (error) {
    return '';
  }
};

const getEntityPhoneNumber = (
  person?: ContactModelV2 | null,
  contacts?: ContactModelV2[] | null,
  unsavedContact?: ContactModelBase | null
): string => {
  try {
    if (person) {
      return person.phoneNumber;
    }

    if (contacts?.[0]) {
      return contacts[0]?.phoneNumber || '';
    }

    if (unsavedContact) {
      return unsavedContact.DisplayName;
    }

    return '';
  } catch (error) {
    return '';
  }
};

const getEntityImage = (
  person?: ContactModelV2 | null,
  contacts?: ContactModelV2[] | null
): string => {
  if (person) {
    return person?.avatarUrl || '';
  }

  if (contacts) {
    const extrContact = contacts[0];
    return extrContact?.avatarUrl || '';
  }

  return '';
};

const getEntityCategory = (
  person?: ContactModelV2 | null,
  contacts?: ContactModelV2[] | null
): string => {
  if (person || contacts?.[0]) {
    return mapContactCategory(person || contacts?.[0]);
  }

  return '';
};

type ConversationType = 'SMS' | 'Instant Messaging';

export interface useEntityInfoProps {
  unsavedContact?: ContactModelBase | null;
  contacts?: ContactModelV2[] | null;
  person?: ContactModelV2 | null;
}

export interface useEntityInfoReturn {
  name: string;
  phoneNumber: string;
  image: string;
  category: string;
  messageStatus: MessageStatusModel;
  presence?: PresenceModel | null;
  isInactive: boolean;
  conversationType: ConversationType | null;
  conversationDestination: string | null;
}

export const useEntityInfo = ({
  unsavedContact,
  contacts,
  person,
}: useEntityInfoProps): useEntityInfoReturn => {
  const {
    ui: uiStore,
    conversation: conversationStore,
    participant: participantStore,
  } = useContext(MobXProviderContext) as RootStoreProps;

  const { conversationType, conversationDestination } = useMemo((): Pick<
    useEntityInfoReturn,
    'conversationType' | 'conversationDestination'
  > => {
    try {
      const currentConversation = conversationStore.CurrentConversation;

      if (!currentConversation) {
        return { conversationType: null, conversationDestination: null };
      }

      const otherParticipants = participantStore.selectOtherParticipants(
        currentConversation.id
      );

      if (!otherParticipants || otherParticipants.length === 0) {
        return { conversationType: null, conversationDestination: null };
      }

      const firstOtherParticipant = otherParticipants[0];

      if (firstOtherParticipant.personId > 0) {
        return {
          conversationType: 'Instant Messaging' as const,
          conversationDestination: null,
        };
      }

      if (firstOtherParticipant.phone) {
        return {
          conversationType: 'SMS' as const,
          conversationDestination: formatNumberWithNationalCode(
            firstOtherParticipant.phone
          ),
        };
      }

      return { conversationType: null, conversationDestination: null };
    } catch (error) {
      return { conversationType: null, conversationDestination: null };
    }
  }, [conversationStore.CurrentConversation, participantStore]);

  const {
    name,
    phoneNumber,
    image,
    category,
    messageStatus,
    isInactive,
  } = useMemo<Partial<useEntityInfoReturn>>(() => {
    try {
      const computedName = getEntityName(person, contacts, unsavedContact);
      const computedPhoneNumber = getEntityPhoneNumber(
        person,
        contacts,
        unsavedContact
      );
      const computedImage = getEntityImage(person, contacts);
      const computedCategory = getEntityCategory(person, contacts);
      const computedMessageStatus =
        person && uiStore.selectPersonMessageStatus(person.sourceId);
      const isInactive = person && person.deleted;

      return {
        name: computedName,
        phoneNumber: computedPhoneNumber,
        image: computedImage,
        category: computedCategory,
        messageStatus: computedMessageStatus,
        isInactive,
      };
    } catch (error) {
      return {
        name: '',
        phoneNumber: '',
        image: '',
        category: '',
        messageStatus: null,
        isInactive: false,
      };
    }
  }, [unsavedContact, contacts, person, uiStore]);

  const presence = person?.sourceId
  ? uiStore.selectPersonPresenceStatus(person.sourceId)
  : null

  return {
    name,
    phoneNumber,
    image,
    category,
    messageStatus,
    presence,
    isInactive,
    conversationType,
    conversationDestination,
  };
};
